import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../ui/Button';
import { useNotifications } from '../../contexts/NotificationsContext';
import { NotificationPopover } from '../notifications/NotificationPopover';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { InstallPwaModal } from '../pwa/InstallPwaModal';
import { QuickCommandPalette } from '../navigation/QuickCommandPalette';
import { Bell, RefreshCw, LogOut, Download, Menu, Search } from 'lucide-react';

interface HeaderProps {
    onToggleSidebar: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const { user, logout } = useAuth();
  const { deferredPrompt, isInstalled, triggerInstallPrompt, isIOS } = useInstallPrompt();
  const { unreadCount } = useNotifications();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  
  const handleHardRefresh = () => {
    window.location.reload();
  };

  const handleInstallClick = () => {
      if (deferredPrompt) {
          triggerInstallPrompt();
      } else {
          setIsInstallModalOpen(true);
      }
  };

  // Keyboard shortcut Ctrl+K / Cmd+K for command palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
          if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
              setIsNotificationOpen(false);
          }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
          document.removeEventListener('mousedown', handleClickOutside);
      };
  }, []);

  return (
    <>
      <header className="bg-surface/90 backdrop-blur-xl h-16 flex items-center justify-between px-4 md:px-6 border-b border-border/40 relative sticky top-0 z-30 shadow-sm">
        <div className="flex items-center space-x-3">
          <button 
            onClick={onToggleSidebar} 
            className="p-2 md:hidden rounded-xl text-text-secondary hover:text-text-main hover:bg-surface-light border border-border/40 transition-colors cursor-pointer"
            aria-label="Menü öffnen"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Quick Search Bar / Command Palette Trigger */}
          <button
            onClick={() => setIsCommandPaletteOpen(true)}
            className="hidden sm:flex items-center gap-2.5 px-3 py-1.5 rounded-xl bg-surface-light/60 hover:bg-surface-light border border-border/40 hover:border-primary/40 text-text-muted hover:text-text-main text-xs transition-all cursor-pointer group shadow-xs"
            title="Schnellsuche öffnen (Strg+K)"
          >
            <Search className="w-3.5 h-3.5 text-primary group-hover:scale-110 transition-transform" />
            <span className="font-medium">Suchen oder Springen...</span>
            <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-surface border border-border/40 text-text-muted">
              Strg+K
            </kbd>
          </button>
        </div>

        <div className="flex items-center space-x-2 md:space-x-3">
          {/* Mobile search button */}
          <button
            onClick={() => setIsCommandPaletteOpen(true)}
            className="sm:hidden p-2.5 rounded-xl bg-surface-light/80 hover:bg-surface-light border border-border/40 text-text-secondary hover:text-text-main transition-all cursor-pointer"
            title="Schnellsuche"
            aria-label="Schnellsuche öffnen"
          >
            <Search className="w-4 h-4" />
          </button>

          {!isInstalled && (
            <Button
              onClick={handleInstallClick}
              variant="primary"
              size="sm"
              className="flex items-center gap-1.5 shadow-glow"
              title="App auf deinem Gerät installieren"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline font-bold">App laden</span>
            </Button>
          )}
          
          <div className="relative" ref={notificationRef}>
            <button 
              onClick={() => setIsNotificationOpen(!isNotificationOpen)} 
              className="p-2.5 rounded-xl bg-surface-light/80 hover:bg-surface-light border border-border/40 text-text-secondary hover:text-text-main relative transition-all cursor-pointer"
              title="Benachrichtigungen"
              aria-label="Benachrichtigungen anzeigen"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 block h-2.5 w-2.5 rounded-full ring-2 ring-surface bg-rose-500 animate-pulse" />
              )}
            </button>
            {isNotificationOpen && <NotificationPopover onClose={() => setIsNotificationOpen(false)} />}
          </div>

          <button 
            onClick={handleHardRefresh} 
            className="p-2.5 rounded-xl bg-surface-light/80 hover:bg-surface-light border border-border/40 text-text-secondary hover:text-text-main hidden sm:flex items-center justify-center transition-all cursor-pointer"
            title="Neu laden"
            aria-label="App neu laden"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          
          <div className="hidden md:flex flex-col text-right pl-2 pr-1 border-l border-border/40">
            <p className="font-semibold text-text-main text-xs truncate max-w-[160px]">{user?.email}</p>
            <p className="text-[10px] text-primary font-bold uppercase tracking-widest">{user?.role}</p>
          </div>

          <button 
            onClick={logout} 
            className="p-2.5 rounded-xl bg-surface-light/80 hover:bg-rose-500/10 border border-border/40 text-text-secondary hover:text-rose-400 transition-all cursor-pointer" 
            title="Abmelden" 
            aria-label="Abmelden"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Global Quick Command Palette */}
      <QuickCommandPalette 
        isOpen={isCommandPaletteOpen} 
        onClose={() => setIsCommandPaletteOpen(false)} 
      />

      <InstallPwaModal 
        isOpen={isInstallModalOpen} 
        onClose={() => setIsInstallModalOpen(false)}
        onInstall={() => {
            triggerInstallPrompt();
            setIsInstallModalOpen(false);
        }}
        isIOS={isIOS}
        canTriggerAutomatic={!!deferredPrompt}
      />
    </>
  );
};
